import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, X, ArrowRight, ArrowLeft, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp, readDemo } from "@/context/AppContext";

const STEPS = [
  { to: "/app", k: "tour_1" }, { to: "/app/sales", k: "tour_2" }, { to: "/app/payments", k: "tour_3" },
  { to: "/app/bank", k: "tour_4" }, { to: "/app/assets", k: "tour_5" }, { to: "/app/closing", k: "tour_6" },
];
const save = (patch) => sessionStorage.setItem("mohassib_demo", JSON.stringify({ ...readDemo(), ...patch }));

export const DemoBar = () => {
  const { t, demo, exitDemo } = useApp();
  const nav = useNavigate();
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.round((demo.exp - Date.now()) / 60000)));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [demo]);
  const restart = () => { save({ tour: 0, tourOff: false }); window.dispatchEvent(new Event("demo-tour")); };
  return (
    <div className="fixed inset-x-0 top-0 z-50 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 bg-[#E30613] px-4 py-1.5 text-xs font-medium text-white" data-testid="demo-bar">
      {demo.branding && <span className="flex items-center gap-1.5 rounded-full bg-white/15 px-2 py-0.5" data-testid="demo-bar-firm">{demo.branding.has_logo && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/demo/links/${demo.branding.slug}/logo`} alt="" className="h-4 w-auto rounded bg-white" />}{t("demo_offered_by")} {demo.branding.firm_name}</span>}
      <span className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" />{t("demo_mode")} · {left} min</span>
      <button onClick={restart} className="underline underline-offset-2" data-testid="demo-tour-restart">{t("demo_tour")}</button>
      <Link to="/sign-up" onClick={exitDemo} className="rounded-full bg-white px-3 py-0.5 font-semibold text-[#B8000F]" data-testid="demo-signup">{t("demo_signup")}</Link>
      <button onClick={() => { exitDemo(); nav("/"); }} className="opacity-80 hover:opacity-100" data-testid="demo-exit">{t("demo_exit")}</button>
    </div>
  );
};

export const DemoTour = () => {
  const { t } = useApp();
  const nav = useNavigate();
  const [st, setSt] = useState(() => readDemo() || {});
  useEffect(() => {
    const h = () => { setSt(readDemo() || {}); nav(STEPS[0].to); };
    window.addEventListener("demo-tour", h);
    return () => window.removeEventListener("demo-tour", h);
  }, [nav]);
  const i = st.tour || 0;
  if (st.tourOff || i >= STEPS.length) return null;
  const go = (n) => {
    if (n >= STEPS.length) { save({ tourOff: true }); setSt({ ...st, tourOff: true }); return; }
    save({ tour: n }); setSt({ ...st, tour: n }); nav(STEPS[n].to);
  };
  const off = () => { save({ tourOff: true }); setSt({ ...st, tourOff: true }); };
  return (
    <div className="fixed bottom-5 end-5 z-50 w-[340px] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl fade-up" data-testid="demo-tour" key={i}>
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E30613] text-white"><Compass className="h-4 w-4" /></span>
        <span className="eyebrow">{t("demo_tour")} · {i + 1}/{STEPS.length}</span>
        <button onClick={off} className="ms-auto text-slate-400 hover:text-slate-700" data-testid="demo-tour-close"><X className="h-4 w-4" /></button>
      </div>
      <h3 className="mt-3 font-semibold text-slate-900" data-testid="demo-tour-title">{t(`${STEPS[i].k}_t`)}</h3>
      <p className="mt-1 text-sm text-slate-600">{t(`${STEPS[i].k}_d`)}</p>
      <div className="mt-4 flex items-center gap-1">
        {STEPS.map((_, j) => <span key={j} className={`h-1.5 flex-1 rounded-full ${j <= i ? "bg-[#E30613]" : "bg-slate-200"}`} />)}
      </div>
      <div className="mt-4 flex justify-between">
        <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => go(i - 1)} data-testid="demo-tour-prev"><ArrowLeft className="h-4 w-4 rtl:rotate-180" /></Button>
        {i < STEPS.length - 1
          ? <Button size="sm" onClick={() => go(i + 1)} data-testid="demo-tour-next">{t("next")}<ArrowRight className="h-4 w-4 ms-1 rtl:rotate-180" /></Button>
          : <Button size="sm" asChild data-testid="demo-tour-finish"><Link to="/sign-up">{t("demo_signup")}</Link></Button>}
      </div>
    </div>
  );
};
